import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { LoadManager } from '@theme/custom-collection-utility';

const EVENT_RECOMMENDATIONS_UPDATED = 'ecomm-recommendations-updated';

function isEcommCollectionRecommendationsEnabled() {
  return /** @type {any} */ (window).themeSettings?.enableEcommCollectionRecommendations !== false;
}

/**
 * Section morph (`morphSection`) inserts `<script src>` from the Section Rendering API into the DOM
 * but those nodes do not execute. Read the same URLs and append fresh scripts to `document.head`.
 * @returns {Promise<void>}
 */
async function ensureEcommRecommendationBundleExecuted() {
  const g = /** @type {any} */ (globalThis);
  if (typeof g.EcommRecommendationsUtils?.detectActivePersonToggle === 'function') return;
  if (!isEcommCollectionRecommendationsEnabled()) return;
  if (!document.querySelector('[data-ecomm-person-toggle-options]')) return;

  const needles = [
    'ecomm-recommendations-utility.js',
    'ecomm-recommendations-algorithm.js',
    'ecomm-recommendations-logged-in.js',
    'ecomm-recommendations-guest.js',
  ];
  /** @type {string[]} */
  const urls = [];
  for (const n of needles) {
    const el = document.querySelector(`script[src*="${n}"]`);
    if (!(el instanceof HTMLScriptElement) || !el.src) {
      return;
    }
    urls.push(el.src);
  }

  if (ensureEcommRecommendationBundleExecuted._inFlight) return ensureEcommRecommendationBundleExecuted._inFlight;

  ensureEcommRecommendationBundleExecuted._inFlight = (async () => {
    try {
      for (const url of urls) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = url;
          s.async = false;
          s.onload = () => resolve(undefined);
          s.onerror = () => reject(new Error(String(url)));
          document.head.appendChild(s);
        });
      }
    } catch {
      /* script load failed; recommendation scripts may stay unavailable */
    } finally {
      ensureEcommRecommendationBundleExecuted._inFlight = null;
    }
  })();

  return ensureEcommRecommendationBundleExecuted._inFlight;
}
ensureEcommRecommendationBundleExecuted._inFlight = /** @type {Promise<void> | null} */ (null);

function attachEcommMorphScriptRecoveryOnce() {
  if (attachEcommMorphScriptRecoveryOnce._did) return;
  attachEcommMorphScriptRecoveryOnce._did = true;
  document.addEventListener('collection-results-rendered', () => {
    void ensureEcommRecommendationBundleExecuted().then(() => {
      document.querySelector('input[name="ecomm-profile"]:checked')?.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}
attachEcommMorphScriptRecoveryOnce._did = false;

/** Coalesce duplicate `ecomm-recommendations-updated` handling when several quick-filter instances are on the page. */
let handleEcommRecommendationsUpdateInFlight = false;

/**
 * @typedef {Object} QuickFiltersRefs
 * @property {HTMLElement[]} quickFilterItems - The quick filter item elements
 */

/**
 * Handles quick filter interactions with improved selector logic and visual feedback
 * 
 * This component automatically works with the new URL-less filter system:
 * - When facets-form-component has `disable-url-updates` attribute: Uses URL-less mode
 * - When no attribute is set: Uses normal mode with URL updates
 * - All filter updates go through the smart updateFilters() method which auto-detects preferences
 * 
 * @extends {Component<QuickFiltersRefs>}
 */
export class QuickFiltersComponent extends Component {
  static ACTIVE_STEP_KEY = 'tv_subscription_active_step_handle';
  static ONEOFF_ENABLED_KEY = 'tv_subscription_oneoff_enabled';

  /**
   * @returns {boolean}
   */
  #isOneoffEnabled() {
    try {
      return String(sessionStorage.getItem(QuickFiltersComponent.ONEOFF_ENABLED_KEY) || '').trim() === '1';
    } catch {
      return false;
    }
  }

  // /**
  //  * When the active step changes (footer, cart modal, or our own soup/dessert toggle),
  //  * sync the person toggle UI. The footer is the single source of truth for sessionStorage;
  //  * we only refresh our UI here.
  //  *
  //  * @param {Event} event
  //  */
  // #handleSubscriptionStepChanged = (event) => {
  //   const detail = /** @type {any} */ (event)?.detail || {};
  //   const handle = String(detail?.handle || '').trim();
  //   if (!handle) return;
  //   this.#syncPersonToggleOptions();
  // }

  /**
   * @returns {string}
   */
  #getActiveStepHandle() {
    try {
      return String(sessionStorage.getItem(QuickFiltersComponent.ACTIVE_STEP_KEY) || '').trim() || 'p1';
    } catch {
      return 'p1';
    }
  }

  /**
   * @param {string} handle
   */
  #setActiveStepHandle(handle) {
    const h = String(handle || '').trim() || 'p1';
    try {
      sessionStorage.setItem(QuickFiltersComponent.ACTIVE_STEP_KEY, h);
    } catch {
      // ignore
    }
  }

  /**
   * @returns {{ profileCount: number; hasSoup: boolean; hasDessert: boolean }}
   */
  #getSurveyStepMeta() {
    try {
      const raw = sessionStorage.getItem('subscription_survey_result');
      const parsed = raw ? JSON.parse(raw) : null;
      const payload = parsed && typeof parsed === 'object' ? parsed : null;
      const profilesLength = Array.isArray(payload?.profiles) ? payload.profiles.length : 0;
      const profileCountFromPayload = Number(payload?.profileCount);
      const profileCount =
        profileCountFromPayload >= 1 && profileCountFromPayload <= 6
          ? profileCountFromPayload
          : Math.min(6, Math.max(1, profilesLength || 1));

      let soupCount = 0;
      let dessertCount = 0;
      const addons = Array.isArray(payload?.addOns) ? payload.addOns : Array.isArray(payload?.add_ons) ? payload.add_ons : [];
      addons.forEach((/** @type {{ addonType?: string; addon_type?: string; quantity?: number }} */ addon) => {
        const t = String(addon?.addonType || addon?.addon_type || '').toLowerCase();
        const q = Number(addon?.quantity) || 0;
        if (t === 'soup') soupCount += q;
        else if (t === 'dessert') dessertCount += q;
      });

      return { profileCount, hasSoup: soupCount > 0, hasDessert: dessertCount > 0 };
    } catch {
      return { profileCount: 1, hasSoup: false, hasDessert: false };
    }
  }

  /** @type {HTMLElement | null | undefined} */
  #recommendationOverlay;
  /** @type {HTMLElement | null | undefined} */
  #recommendationStartButton;
  /** @type {HTMLElement | null | undefined} */
  #recommendationCloseButton;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    attachEcommMorphScriptRecoveryOnce();

    this.quickFilterItems = this.querySelectorAll('.quick-filter-item');
    this.#recommendationOverlay =
    /** @type {HTMLElement | null} */ (this.querySelector('[data-recommendation-overlay]'));
    this.#recommendationStartButton =
    /** @type {HTMLElement | null} */ (this.querySelector('[data-recommendation-start]'));
    this.#recommendationCloseButton =
    /** @type {HTMLElement | null} */ (this.querySelector('[data-recommendation-close]'));

    if (this.#recommendationStartButton instanceof HTMLElement) {
      this.#recommendationStartButton.addEventListener('click', () => {
        this.#toggleRecommendationOverlay(false);
        this.toggleRecommendedFilter();
      });
    }

    // if you click on the close button, close the overlay
    if (this.#recommendationCloseButton instanceof HTMLElement) {
      this.#recommendationCloseButton.addEventListener('click', () => {
        this.#toggleRecommendationOverlay(false);
      });
    }

    this.#init();

    // Listen for custom sync event
    document.addEventListener('sync-quick-filters', this.#handleSyncEvent);
    // Listen for filter updates to sync person toggle options
    document.addEventListener('sync-quick-filters', this.#syncPersonToggleOptions);
    window.addEventListener('subscription-survey-completed', this.#syncPersonToggleOptions);
    window.addEventListener('tv-subscription-step-changed', this.#syncPersonToggleOptions);
    // Listen for recommendations updated event
    window.addEventListener(EVENT_RECOMMENDATIONS_UPDATED, this.#handleRecommendationsUpdated);

    // On pages that load directly into an active recommendation view, make the
    // checked Person 1 radio go through the same path as a manual toggle.
    requestAnimationFrame(() => this.#syncPersonToggleOptions());
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.FilterUpdate, this.#syncQuickFilterStates);
    document.removeEventListener('sync-quick-filters', this.#handleSyncEvent);
    document.removeEventListener('sync-quick-filters', this.#syncPersonToggleOptions);
    window.removeEventListener('subscription-survey-completed', this.#syncPersonToggleOptions);
    window.removeEventListener('tv-subscription-step-changed', this.#syncPersonToggleOptions);
    window.removeEventListener(EVENT_RECOMMENDATIONS_UPDATED, this.#handleRecommendationsUpdated);
  }

  /**
   * Initialize the quick filters component
   */
  #init() {
    if (this.quickFilterItems?.length === 0) return;

    this.quickFilterItems?.forEach(quickFilter => {
      quickFilter.addEventListener('click', this.#handleQuickFilterClick.bind(this));
      quickFilter.addEventListener('keydown', this.#handleQuickFilterKeydown.bind(this));
    });

    // Listen for filter changes to update quick filter states
    document.addEventListener(ThemeEvents.FilterUpdate, this.#syncQuickFilterStates);

    // Initialize quick filter states based on current filter states
    this.#syncQuickFilterStates();
  }

  /**
   * Synchronizes quick filter visual states with actual filter states
   */
  #syncQuickFilterStates = () => {
    const quickFilterItems = document.querySelectorAll('.quick-filter-item');
    // Check if any filters are active in the filters drawer if not make "Alle" appear selected.
    const filtersDrawer = document.querySelector('#filters-drawer');
    const hasAnyActiveFilter =
      filtersDrawer instanceof HTMLElement
        ? Boolean(filtersDrawer.querySelector('input[type="checkbox"]:checked'))
        : false;


    quickFilterItems.forEach(quickFilter => {
      if (quickFilter instanceof HTMLElement) {
        const filterId = quickFilter.getAttribute('data-filter-id');

        if (filterId) {
          const filterInput = this.#findFilterInput(filterId);

          if (filterInput) {
            this.#updateQuickFilterVisualState(quickFilter, filterInput.checked);
          } else {
            // If filter input not found, ensure quick filter is in inactive state
            this.#updateQuickFilterVisualState(quickFilter, false);
          }
        }
      }
    });
  };


  /**
   * Handles the custom sync event
   */
  #handleSyncEvent = () => {
    this.#syncQuickFilterStates();
  };

  /**
   * Handles quick filter click events
   * @param {Event} event - The click event
   */
  async #handleQuickFilterClick(event) {
    if (!(event.currentTarget instanceof HTMLElement)) return;
    await this.#handleQuickFilterActivate(event.currentTarget, event);
  }

  /**
   * Clears all active filters except inventory/product_type/availability.
   * Used when enabling "Aanbevolen" so it doesn't combine with diet/etc filters.
   * Does not call `custom-collection-flow` (no `flow-visual-update-requested`): that retriggers
   * the footer's `#applyStepFiltering` / `updateFiltersWithBestMethod` and races this path.
   * @returns {Promise<boolean>} True if any checkbox was unchecked.
   */
  async #clearAllFiltersExceptInventoryAndProductType() {
    if (!isEcommCollectionRecommendationsEnabled()) return false;
    const drawer = document.querySelector('#filters-drawer');
    const form = drawer?.querySelector('facets-form-component form');
    if (!(form instanceof HTMLFormElement)) return false;

    /** @param {Element} input */
    const keep = (input) => {
      if (!(input instanceof HTMLInputElement)) return false;
      const name = input.name || '';
      const label = input.dataset?.label || '';
      if (label === 'Aanbevolen') return true;
      return (
        name.includes('inventory_levels') ||
        name.includes('custom.product_type') ||
        name.includes('availability')
      );
    };

    let didClearAny = false;
    const inputs = Array.from(form.querySelectorAll('input[type="checkbox"]:checked'));
    inputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (!keep(input)) {
        input.checked = false;
        didClearAny = true;
      }
    });

    return didClearAny;
  }

  #handleRecommendationsUpdated = async () => {
    if (!isEcommCollectionRecommendationsEnabled()) return;
    if (handleEcommRecommendationsUpdateInFlight) return;

    handleEcommRecommendationsUpdateInFlight = true;
    try {
      const aanbevolenInput = this.#findFilterInput('Aanbevolen');
      const alleInput = this.#findFilterInput("Alle");

      if (alleInput?.checked) {
        alleInput.checked = false;
      }

      // When Aanbevolen is being activated, clear all other filters except: inventory level filters, custom.product_type filters (step filters), availability filters
      await this.#clearAllFiltersExceptInventoryAndProductType();

      if (aanbevolenInput) {
        aanbevolenInput.checked = true;
        await this.#triggerFilterUpdate(aanbevolenInput);
      }
    } finally {
      this.#syncPersonToggleOptions();
      handleEcommRecommendationsUpdateInFlight = false;
    }
  }

  #syncPersonToggleOptions = () => {
    const sessionRecommendationsPayload = sessionStorage.getItem('recommendations');
    const loggedInMijnGegevensEl = document.querySelector('[data-ecomm-mijn-gegevens-json]');

    /** @type {any|null} */
    let payload = null;
    if (loggedInMijnGegevensEl instanceof HTMLElement) {
      try {
        payload = JSON.parse(String(loggedInMijnGegevensEl.textContent || '').trim() || '{}');
      } catch (error) {
        payload = null;
      }
    } else if (sessionRecommendationsPayload) {
      try {
        payload = JSON.parse(sessionRecommendationsPayload);
      } catch (error) {
        payload = null;
      }
    }

    // Person → recommendation scripts: must not depend on payload. Previously listeners were only
    // registered after `if (!payload) return`, so toggling people never fired `ecomm-profile-changed`.
    const toggleOptionsForListeners = document.querySelector('[data-ecomm-person-toggle-options]');
    if (toggleOptionsForListeners instanceof HTMLElement) {
      const listenerRoot = toggleOptionsForListeners.closest('[data-ecomm-person-toggle]');
      const listenerMode =
        (listenerRoot instanceof HTMLElement ? String(listenerRoot.dataset?.ecommToggleMode || '').trim() : '') || 'ecomm';
      if (listenerMode !== 'subscription' && !toggleOptionsForListeners.dataset.tvEcommProfileToggleInitialized) {
        toggleOptionsForListeners.dataset.tvEcommProfileToggleInitialized = '1';
        const slotNodes = Array.from(
          toggleOptionsForListeners.querySelectorAll('[data-ecomm-profile-slot]')
        ).filter((el) => el instanceof HTMLElement);
        const handleEcommProfileChange = (/** @type {Event} */ event) => {
          const target = event.target;
          if (!(target instanceof HTMLInputElement) || target.name !== 'ecomm-profile') return;
          try {
            const handle =
              target.closest('[data-ecomm-step-handle]')?.getAttribute('data-ecomm-step-handle') || target.value;
            window.dispatchEvent(new CustomEvent('tv-subscription-step-changed', { detail: { handle } }));

            const displayName =
              target.labels && target.labels[0]
                ? String(target.labels[0].textContent || '').trim()
                : '';
            const profileDetail = {
              profileKey: target.value,
              displayName,
              id: target.id,
              source: 'change',
            };

            const emitEcommProfileChanged = () => {
              window.dispatchEvent(new CustomEvent('ecomm-profile-changed', { detail: profileDetail }));
              // Parent `initPersonToggleLogger` would dispatch again — guest runs twice / races.
              event.stopPropagation();
            };

            const g = /** @type {any} */ (window);
            if (typeof g.EcommRecommendationsUtils?.detectActivePersonToggle !== 'function') {
              void ensureEcommRecommendationBundleExecuted().then(emitEcommProfileChanged);
            } else {
              emitEcommProfileChanged();
            }
          } catch {
            // ignore
          }
        };
        slotNodes.forEach((slotEl) => {
          const input = slotEl.querySelector('input[name="ecomm-profile"]');
          if (input instanceof HTMLInputElement) {
            input.addEventListener('change', handleEcommProfileChange);
          }
        });
      }
    }

    if (!payload) return;
    try {
      const profiles = Array.isArray(payload?.profiles) ? payload.profiles : [];

      // Find the person toggle options container
      const toggleOptionsElement = document.querySelector("[data-ecomm-person-toggle-options]");
      if (!(toggleOptionsElement instanceof HTMLElement)) return;

      const toggleRoot = toggleOptionsElement.closest('[data-ecomm-person-toggle]');
      const toggleMode = (toggleRoot instanceof HTMLElement ? String(toggleRoot.dataset?.ecommToggleMode || '') : '').trim() || 'ecomm';

      const slotElements = Array.from(toggleOptionsElement.querySelectorAll('[data-ecomm-profile-slot]')).filter(
        (el) => el instanceof HTMLElement
      );

      // Non-subscription collections: show all relevant profiles at once, default to first.
      if (toggleMode !== 'subscription') {
        slotElements.forEach((slotElement, index) => {
          if (!(slotElement instanceof HTMLElement)) return;

          const profile = profiles[index];
          const inputElement = slotElement.querySelector('input[name="ecomm-profile"]');
          const labelElement = slotElement.querySelector('label[data-ecomm-profile-label]');
          if (!(inputElement instanceof HTMLInputElement) || !(labelElement instanceof HTMLElement)) return;

          const hasProfile = Boolean(profile);
          if (!hasProfile && profiles.length > 0) {
            slotElement.classList.add('no-disp');
            inputElement.disabled = true;
            return;
          }

          // If we don't have payload profiles (fallback scenario), keep Liquid defaults.
          if (hasProfile) {
            const expectedHandle = `p${index + 1}`;
            const profileKey = String(profile?.profileKey || '').trim() || expectedHandle;
            const displayName = String(profile?.displayName || '').trim();
            inputElement.disabled = false;
            inputElement.value = profileKey;
            if (displayName) labelElement.textContent = displayName;
          } else {
            inputElement.disabled = false;
          }

          slotElement.classList.remove('no-disp');
        });

        // Ensure one profile is selected by default (first visible).
        const visibleProfileInputs = slotElements
          .filter((el) => el instanceof HTMLElement && !el.classList.contains('no-disp'))
          .map((el) => el.querySelector('input[name="ecomm-profile"]'))
          .filter((el) => el instanceof HTMLInputElement);

        const anyChecked = visibleProfileInputs.some((i) => i.checked);
        if (!anyChecked && visibleProfileInputs[0] instanceof HTMLInputElement) {
          visibleProfileInputs[0].checked = true;
          visibleProfileInputs[0].dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Hide soup/dessert toggles if present (shouldn't render on non-subscription templates).
        const soupEl = toggleOptionsElement.querySelector('[data-ecomm-step="soup"]');
        if (soupEl instanceof HTMLElement) soupEl.classList.add('no-disp');
        const dessertEl = toggleOptionsElement.querySelector('[data-ecomm-step="dessert"]');
        if (dessertEl instanceof HTMLElement) dessertEl.classList.add('no-disp');
        const oneoffEl = toggleOptionsElement.querySelector('[data-ecomm-step="oneoff"]');
        if (oneoffEl instanceof HTMLElement) oneoffEl.classList.add('no-disp');

        // Show/hide the entire toggle if there are no profiles
        const toggleElement = document.querySelector('[data-ecomm-person-toggle]');
        if (toggleElement instanceof HTMLElement) {
          if (profiles.length < 1) {
            toggleElement.classList.add('no-disp');
          } else {
            toggleElement.classList.remove('no-disp');
          }
        }

        // Initialize person toggle logger
        const windowAny = /** @type {any} */ (window);
        const recommendationsUtils = windowAny.EcommRecommendationsUtils;
        if (recommendationsUtils && typeof recommendationsUtils.initPersonToggleLogger === 'function') {
          recommendationsUtils.initPersonToggleLogger(toggleOptionsElement, { forceDetect: true });
        }

        return;
      }

      const { profileCount, hasSoup, hasDessert } = this.#getSurveyStepMeta();
      const hasOneoff = this.#isOneoffEnabled();

      // Determine which step should be visible (default p1)
      let activeHandle = this.#getActiveStepHandle();
      const allowedHandles = [
        ...Array.from({ length: Math.max(1, Math.min(6, profileCount)) }, (_, i) => `p${i + 1}`),
        ...(hasSoup ? ['soup'] : []),
        ...(hasDessert ? ['dessert'] : []),
        ...(hasOneoff ? ['oneoff'] : []),
      ];
      if (!allowedHandles.includes(activeHandle)) activeHandle = allowedHandles[0] || 'p1';
      this.#setActiveStepHandle(activeHandle);

      slotElements.forEach((slotElement, index) => {
        if (!(slotElement instanceof HTMLElement)) return;

        const isAllowed = index < profileCount;
        const expectedHandle = `p${index + 1}`;

        const profile = profiles[index];
        const inputElement = slotElement.querySelector('input[name="ecomm-profile"]');
        const labelElement = slotElement.querySelector('label[data-ecomm-profile-label]');
        if (!(inputElement instanceof HTMLInputElement) || !(labelElement instanceof HTMLElement)) return;

        const profileKey = String(profile?.profileKey || '').trim() || expectedHandle;
        const displayName = String(profile?.displayName || '').trim();

        // Permanently discard unused togglers (based on survey result)
        if (!isAllowed) {
          slotElement.classList.add('no-disp');
          inputElement.disabled = true;
          return;
        }

        inputElement.disabled = false;
        inputElement.value = profileKey;
        if (displayName) labelElement.textContent = displayName;

        // Update the subscription label heading based on the real profile count (important
        // for guest users where server-side profile_count is always 1).
        const subscriptionLabel = slotElement.querySelector('.person-toggle-item-label--subscription');
        const headingEl = subscriptionLabel instanceof HTMLElement
          ? subscriptionLabel.querySelector('.subscription-step-label-heading')
          : null;
        if (headingEl instanceof HTMLElement && subscriptionLabel instanceof HTMLElement) {
          const personName = displayName || `Persoon ${index + 1}`;
          if (profileCount > 1) {
            const prefix = String(subscriptionLabel.dataset.subHeadingMultiplePrefix || '');
            if (prefix) headingEl.textContent = prefix + personName;
            // If prefix attribute is absent, leave the server-rendered heading untouched.
          } else {
            const single = String(subscriptionLabel.dataset.subHeadingSingle || '');
            if (single) headingEl.textContent = single;
          }
        }

        // Show only the active step; hide all others.
        const shouldShow = activeHandle === expectedHandle;
        slotElement.classList.toggle('no-disp', !shouldShow);

        if (shouldShow && !inputElement.checked) {
          inputElement.checked = true;
          inputElement.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      // Soup/Dessert step toggles (radio+label; does not change ecomm-profile)
      const soupEl = toggleOptionsElement.querySelector('[data-ecomm-step="soup"]');
      if (soupEl instanceof HTMLElement) {
        const input = soupEl.querySelector('input[name="ecomm-step"][value="soup"]');
        if (input instanceof HTMLInputElement) {
          input.disabled = !hasSoup;
          if (hasSoup && activeHandle === 'soup') input.checked = true;
        }
        soupEl.classList.toggle('no-disp', !(hasSoup && activeHandle === 'soup'));
      }
      const dessertEl = toggleOptionsElement.querySelector('[data-ecomm-step="dessert"]');
      if (dessertEl instanceof HTMLElement) {
        const input = dessertEl.querySelector('input[name="ecomm-step"][value="dessert"]');
        if (input instanceof HTMLInputElement) {
          input.disabled = !hasDessert;
          if (hasDessert && activeHandle === 'dessert') input.checked = true;
        }
        dessertEl.classList.toggle('no-disp', !(hasDessert && activeHandle === 'dessert'));
      }

      const oneoffEl = toggleOptionsElement.querySelector('[data-ecomm-step="oneoff"]');
      if (oneoffEl instanceof HTMLElement) {
        const input = oneoffEl.querySelector('input[name="ecomm-step"][value="oneoff"]');
        if (input instanceof HTMLInputElement) {
          input.disabled = !hasOneoff;
          if (hasOneoff && activeHandle === 'oneoff') input.checked = true;
        }
        oneoffEl.classList.toggle('no-disp', !(hasOneoff && activeHandle === 'oneoff'));
      }

      // Allow manual step toggle for soup/dessert/oneoff while keeping footer in sync
      if (!toggleOptionsElement.dataset.tvStepToggleInitialized) {
        toggleOptionsElement.dataset.tvStepToggleInitialized = '1';
        toggleOptionsElement.addEventListener('change', (event) => {
          const target = event.target;
          if (!(target instanceof HTMLInputElement)) return;
          if (target.name !== 'ecomm-step') return;
          const nextHandle = String(target.value || '').trim();
          if (!nextHandle) return;
          // Footer (capture) will update sessionStorage and sync filters; we only dispatch.
          try {
            window.dispatchEvent(new CustomEvent('tv-subscription-step-changed', { detail: { handle: nextHandle } }));
          } catch {
            // ignore
          }
        });
      }

      // Hide the entire toggle if there's only one profile or less
      const toggleElement = document.querySelector('[data-ecomm-person-toggle]');
      if (toggleElement instanceof HTMLElement) {
        if (profiles.length < 1) {
          toggleElement.classList.add('no-disp');
        } else {
          toggleElement.classList.remove('no-disp');
        }
      }

      // Initialize person toggle logger
      const windowAny = /** @type {any} */ (window);
      const recommendationsUtils = windowAny.EcommRecommendationsUtils;
      if (recommendationsUtils && typeof recommendationsUtils.initPersonToggleLogger === 'function') {
        recommendationsUtils.initPersonToggleLogger(toggleOptionsElement, { forceDetect: true });
      }
    } catch (error) {
      console.error("Error syncing person toggle options", error);
    }
  }

  /**
   * Handles quick filter keyboard events
   * @param {KeyboardEvent} event - The keyboard event
   */
  async #handleQuickFilterKeydown(event) {
    // Activate on Enter or Space key
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    if (!(event.currentTarget instanceof HTMLElement)) return;
    await this.#handleQuickFilterActivate(event.currentTarget, event);
  }

  /**
   * Shared activation logic for click + keyboard so behavior stays identical.
   * Keeps "Aanbevolen exclusivity" rules the same:
   * - If Aanbevolen is active and user activates a different quick filter (not Alle),
   *   switch to Alle, uncheck Aanbevolen, and keep clicked filter checked.
   * - If user activates Aanbevolen without recommendations payload, open overlay and do nothing else.
   * - When enabling Aanbevolen, clear other filters except inventory/product_type/(optionally) availability.
   * - Maintain simple mutual exclusivity between Alle and Aanbevolen.
   * @param {HTMLElement} el
   * @param {Event} event
   */
  async #handleQuickFilterActivate(el, event) {
    const filterId = el.getAttribute('data-filter-id');
    if (!filterId) return;
    if (
      (filterId === 'Aanbevolen' || filterId === 'Alle') &&
      !isEcommCollectionRecommendationsEnabled()
    ) {
      return;
    }

    // If "Aanbevolen" is active and user activates any OTHER quick filter (except Alle),
    // switch to "Alle", uncheck "Aanbevolen", and keep the activated filter selected.
    if (filterId !== 'Aanbevolen' && filterId !== 'Alle') {
      const aanbevolenInput = this.#findFilterInput('Aanbevolen');
      if (aanbevolenInput?.checked) {
        const alleInput = this.#findFilterInput('Alle');
        const clickedInput = this.#findFilterInput(filterId);

        // If we can't find the clicked input, fall back to normal behavior.
        if (!clickedInput) return;

        let didChange = false;
        if (alleInput && !alleInput.checked) {
          alleInput.checked = true;
          didChange = true;
        }
        if (aanbevolenInput.checked) {
          aanbevolenInput.checked = false;
          didChange = true;
        }
        if (!clickedInput.checked) {
          clickedInput.checked = true;
          didChange = true;
        }

        if (didChange) {
          await this.#triggerFilterUpdate(clickedInput);
        }
        return;
      }
    }

    // Aanbevolen overlay gating: only allow enabling Aanbevolen if recommendations payload exists.
    if (filterId === 'Aanbevolen') {
      const checkIfloggedinCustomerExists = Boolean(document.querySelector('[data-ecomm-mijn-gegevens-json]'));

      const checkIfloggedinCustomer = (() => {
        const el = document.querySelector('[data-ecomm-mijn-gegevens-json]');
        if (!(el instanceof HTMLElement)) return false;
        const raw = String(el.textContent || '').trim();
        if (!raw) return false;
        try {
          const parsed = JSON.parse(raw);
          return !!(parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length);
        } catch {
          return false;
        }
      })();

      // If the user is not logged in and there is no recommendations payload, show the overlay
      if (!sessionStorage.getItem('recommendations') && !checkIfloggedinCustomerExists) {
        event.preventDefault();
        this.#toggleRecommendationOverlay();
        return;
      }

      // If the user is logged in and there is no recommendations payload, show the overlay
      else if((checkIfloggedinCustomerExists && !checkIfloggedinCustomer) && !sessionStorage.getItem('recommendations')) {
        event.preventDefault();
        this.#toggleRecommendationOverlay();
        return;
      }

      const aanbevolenInput = this.#findFilterInput('Aanbevolen');
      if (!(aanbevolenInput instanceof HTMLInputElement)) return;

      const needsLoader = !aanbevolenInput.checked;
      if (needsLoader) LoadManager.addLoader();
      try {
        const cleared = await this.#clearAllFiltersExceptInventoryAndProductType();
        const alleInput = this.#findFilterInput('Alle');
        let changed = cleared;
        if (alleInput instanceof HTMLInputElement && alleInput.checked) {
          alleInput.checked = false;
          changed = true;
        }
        if (!aanbevolenInput.checked) {
          aanbevolenInput.checked = true;
          changed = true;
        }
        if (changed) {
          await this.#triggerFilterUpdate(aanbevolenInput);
        } else {
          this.#syncQuickFilterStates();
        }
      } finally {
        if (needsLoader) {
          window.setTimeout(() => LoadManager.removeLoader(), 150);
        }
      }

      return;
    }

    if (filterId === 'Alle') {
      const aanbevolenInput = this.#findFilterInput('Aanbevolen');
      const alleInput = this.#findFilterInput('Alle');
      if (!(alleInput instanceof HTMLInputElement)) {
        await this.#toggleQuickFilter(el);
        return;
      }
      if (alleInput.checked && !(aanbevolenInput instanceof HTMLInputElement && aanbevolenInput.checked)) {
        this.#syncQuickFilterStates();
        return;
      }
      let changed = false;
      if (aanbevolenInput instanceof HTMLInputElement && aanbevolenInput.checked) {
        aanbevolenInput.checked = false;
        changed = true;
      }
      if (!alleInput.checked) {
        alleInput.checked = true;
        changed = true;
      }
      if (changed) {
        await this.#triggerFilterUpdate(alleInput);
      } else {
        this.#syncQuickFilterStates();
      }
      return;
    }

    await this.#toggleQuickFilter(el);
  }

  toggleRecommendedFilter() {

    // Find the survey form component and open it (non-intrusive to existing logic)
    const surveyForm = document.querySelector('thuysvers-subscription-survey-component') || document.querySelector('thuysvers-survey-form-component');
    if (surveyForm && typeof /** @type {any} */ (surveyForm).showSurveyForm === 'function') {
      /** @type {any} */ (surveyForm).showSurveyForm();
    } else if (surveyForm instanceof HTMLElement) {
      // Fallback: simply remove the hidden class if method isn't available
      surveyForm.classList.remove('no-disp');
      document.documentElement.setAttribute('scroll-lock', '');
    }
  }

  /**
   * Toggles a quick filter's state
   * @param {HTMLElement} quickFilter - The quick filter element
   */
  async #toggleQuickFilter(quickFilter) {
    const filterId = quickFilter.getAttribute('data-filter-id');

    if (!filterId) {
      // console.warn('Quick filter missing data-filter-id attribute');
      return;
    }

    // Find the corresponding filter input across all forms
    const filterInput = this.#findFilterInput(filterId);

    if (filterInput) {
      // Toggle the filter state
      const wasChecked = filterInput.checked;
      filterInput.checked = !wasChecked;

      // Trigger the filter update through the proper form component and wait for completion
      await this.#triggerFilterUpdate(filterInput);

      // Note: Quick filter states will be synced automatically after section rendering completes
    } else {
      // console.warn(`No filter input found for quick filter: ${filterId}`);
    }
  }

  /**
   * Finds filter input across all available forms
   * @param {string} filterId - The filter identifier
   * @returns {HTMLInputElement|null} The filter input element
   */
  #findFilterInput(filterId) {
    // Try multiple selector strategies for maximum compatibility
    const selector = `input[data-label="${filterId}"]`;

    // Search in all possible form contexts
    const formContext = '#filters-drawer';

    const input = document.querySelector(`${formContext} ${selector}`);
    if (input instanceof HTMLInputElement) {
      return input;
    }

    return null;
  }


  /**
   * Triggers filter update through the form component
   * @param {HTMLInputElement} filterInput - The filter input that was changed
   * @returns {Promise<void>} Promise that resolves when filter update is complete
   */
  async #triggerFilterUpdate(filterInput) {
    // Find the parent facets form component
    const facetsFormComponent = filterInput.closest('facets-form-component');

    if (facetsFormComponent && 'updateFilters' in facetsFormComponent && typeof facetsFormComponent.updateFilters === 'function') {
      // Check if URL updates are disabled for logging purposes
      const disableURLUpdates = facetsFormComponent.hasAttribute && facetsFormComponent.hasAttribute('disable-url-updates') ||
        (/** @type {any} */ (facetsFormComponent).disableURLUpdates !== undefined && /** @type {any} */ (facetsFormComponent).disableURLUpdates);

      if (disableURLUpdates) {
        // console.log('🔍 [QUICK-FILTERS] Using URL-less filter update mode');
      } else {
        // console.log('🔍 [QUICK-FILTERS] Using normal filter update mode with URL updates');
      }

      // updateFilters is now properly async and returns a Promise
      // It automatically detects URL update preferences and uses the appropriate method
      await facetsFormComponent.updateFilters();

      // console.log('✅ [QUICK-FILTERS] Filter update completed successfully');
    } else {
      // Fallback: dispatch change event to trigger any listeners
      // console.warn('⚠️ [QUICK-FILTERS] No facets form component found, using fallback method');
      filterInput.dispatchEvent(new Event('change', { bubbles: true }));
      // Small delay for any synchronous handlers
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  /**
   * Updates visual state of quick filter
   * @param {HTMLElement} quickFilter - The quick filter element
   * @param {boolean} isActive - Whether the filter is active
   */
  #updateQuickFilterVisualState(quickFilter, isActive) {
    if (isActive) {
      quickFilter.classList.add('quick-filter-item--active');
      quickFilter.setAttribute('aria-pressed', 'true');
    } else {
      quickFilter.classList.remove('quick-filter-item--active');
      quickFilter.setAttribute('aria-pressed', 'false');
    }
  }

  /**
   * Toggles the recommendation overlay visibility
   * @param {boolean} [forceVisible] - Optional flag to force visibility on/off
   */
  #toggleRecommendationOverlay(forceVisible) {
    if (!(this.#recommendationOverlay instanceof HTMLElement)) return;

    const isCurrentlyVisible = this.#recommendationOverlay.classList.contains('quick-filters-recommendation-overlay--visible');
    const shouldShow = typeof forceVisible === 'boolean' ? forceVisible : !isCurrentlyVisible;

    if (shouldShow) {
      this.#recommendationOverlay.classList.add('quick-filters-recommendation-overlay--visible');
      this.#recommendationOverlay.setAttribute('aria-hidden', 'false');

      if (this.#recommendationStartButton instanceof HTMLElement) {
        this.#recommendationStartButton.focus();
      }
    } else {
      this.#recommendationOverlay.classList.remove('quick-filters-recommendation-overlay--visible');
      this.#recommendationOverlay.setAttribute('aria-hidden', 'true');
    }
  }

}

if (!customElements.get('quick-filters-component')) {
  customElements.define('quick-filters-component', QuickFiltersComponent);
}